import React from 'react';
import { Alert } from 'reactstrap';
import { useSelector } from 'react-redux';
import { getTranslationKey } from '../../modules/utils';
import { IShopSalesAutomation, IStoreState } from '../../modules/types';
import { getPricingGroups, getProductUserPricing, getUser } from '../../modules/selectors';
import {
  useGlobalShopSettings,
  useSalesAutomationFormState,
  useSalesAutomationValidation
} from '../../hooks/shop';
import {
  EmailPackageSection,
  CouponCodeAdvertisingSection,
  AnniversaryCouponSection,
  AnniversaryDatePickerSection,
  AbandonedShoppingCartSection,
  EmptyCartProductSuggestionSection,
  FreeShippingSection,
  CouponCodeAfterPurchaseSection,
  MarketingPhotobooksSection
} from './sections';

interface IProps {
  isShopActive: boolean;
  updateCallback?: (data: IShopSalesAutomation, patch?: Partial<IShopSalesAutomation>) => void;
  values: IShopSalesAutomation;
  debounceUpdates?: boolean;
  updateWithInvalidValues?: boolean;
  isCollectionLevelSetting?: boolean;
  isGlobalLevelSetting?: boolean;
  isOnlyDigitalShopActive?: boolean;
  scheduleType?: 'datepicker' | 'input';
  anniversary?: { dateSelection?: boolean; coupon?: boolean };
}

export const SalesAutomationForm: React.FC<IProps> = ({
  isShopActive = false,
  updateCallback,
  values,
  debounceUpdates = true,
  updateWithInvalidValues = false,
  isCollectionLevelSetting = false,
  isOnlyDigitalShopActive = false,
  isGlobalLevelSetting = false,
  scheduleType = 'input',
  anniversary
}) => {
  const { dateSelection, coupon } = anniversary || {};
  const { selectionOptions } = useSelector((state: IStoreState) => state.emailSettings);
  const productUserPriceList = useSelector(getProductUserPricing);
  const pricingGroups = useSelector(getPricingGroups);
  const user = useSelector(getUser);

  // At collection level CollectionShopSettings already fetches the global shop
  // settings, so avoid dispatching getGlobalShopSettingsForCurrentUser twice.
  const { isGlobalShopActive } = useGlobalShopSettings({ fetch: !isCollectionLevelSetting });

  const {
    automationState,
    couponStartDateChanged,
    couponEndDateChanged,
    updateFormValue,
    updateStartDate,
    updateEndDate,
    updateAnniversaryDate,
    dateFromDays
  } = useSalesAutomationFormState(values, {
    debounceUpdates,
    updateWithInvalidValues,
    updateCallback
  });

  const { isOneCouponUsedTwice, isOneAnniversaryCouponUsedTwice, isStrategiesOverlap } =
    useSalesAutomationValidation(automationState);

  const isFormDisabled = !isShopActive;

  const activePhotobookGroups = pricingGroups.filter(
    (group) => group.active && group._productGroup?.includes('photobook')
  );
  const hasPhotobooks = productUserPriceList.some((pricing) => {
    if (!pricing.active) {
      return false;
    }

    const labGroup = activePhotobookGroups.find(
      (group) => pricing._product?._productGroup?._id === group._productGroup
    );

    if (!labGroup) {
      return false;
    }

    return pricing._product?.laboratory === labGroup._laboratory;
  });

  return (
    <>
      {(!isShopActive || !isGlobalShopActive) && isCollectionLevelSetting && (
        <Alert color="warning" style={{ maxWidth: '350px' }}>
          {getTranslationKey('collectionShop.disabledShopWarning')}
        </Alert>
      )}

      <EmailPackageSection
        emailPackage={automationState?.emailPackage}
        selectionOptions={selectionOptions}
        updateFormValue={updateFormValue}
      />

      <CouponCodeAdvertisingSection
        couponCodeAdvertising={automationState.couponCodeAdvertising}
        isFormDisabled={isFormDisabled}
        scheduleType={scheduleType}
        couponStartDateChanged={couponStartDateChanged}
        couponEndDateChanged={couponEndDateChanged}
        isStrategiesOverlap={isStrategiesOverlap}
        dateFromDays={dateFromDays}
        updateFormValue={updateFormValue}
        updateStartDate={updateStartDate}
        updateEndDate={updateEndDate}
      />

      {coupon && (
        <AnniversaryCouponSection
          anniversaryCouponCodeAdvertising={automationState.anniversaryCouponCodeAdvertising}
          isFormDisabled={isFormDisabled}
          updateFormValue={updateFormValue}
        />
      )}

      {scheduleType === 'datepicker' && dateSelection && coupon && (
        <AnniversaryDatePickerSection
          anniversaryCouponCodeAdvertising={automationState.anniversaryCouponCodeAdvertising}
          updateAnniversaryDate={updateAnniversaryDate}
        />
      )}

      <AbandonedShoppingCartSection
        abandonedShoppingCartReminder={automationState.abandonedShoppingCartReminder}
        updateFormValue={updateFormValue}
      />

      <EmptyCartProductSuggestionSection
        emptyCartProductSuggestion={automationState.emptyCartProductSuggestion}
        updateFormValue={updateFormValue}
      />

      <FreeShippingSection
        freeShipping={automationState.freeShipping}
        currency={user.currency}
        updateFormValue={updateFormValue}
      />

      <CouponCodeAfterPurchaseSection
        couponCodeAfterPurchase={automationState.couponCodeAfterPurchase}
        isOneCouponUsedTwice={isOneCouponUsedTwice}
        isOneAnniversaryCouponUsedTwice={isOneAnniversaryCouponUsedTwice}
        updateFormValue={updateFormValue}
      />

      {!isGlobalLevelSetting && (
        <MarketingPhotobooksSection
          marketingPhotobooks={automationState.marketingPhotobooks}
          hasPhotobooks={hasPhotobooks}
          isOnlyDigitalShopActive={isOnlyDigitalShopActive}
          isCollectionLevelSetting={isCollectionLevelSetting}
          updateFormValue={updateFormValue}
        />
      )}
    </>
  );
};
